import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  Checkbox,
  AccordionDetails,
  TableRow,
  useTheme,
  tableCellClasses,
  TableCell,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Collapse,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import KeyIcon from "@mui/icons-material/VpnKey";
import ButtonContained from "../../../Common/ButtonContained";
import ButtonOutlined from "../../../Common/ButtonOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SecurityIcon from "@mui/icons-material/Security";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { showLoader } from "../../../../Redux/Slices/loaderSlice";
import ErrorHandler, {
  capitalizeFirstLetterOfWords,
  checkAccess,
  CommonChip,
  CustomTooltip,
} from "../../../../Utils/handler";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  active_inactive_member,
  active_inactive_member_Deactive,
  add_extra_userType,
  add_role_access,
  addRole,
  delete_extra_userType,
  get_available_access,
  get_dynamic_role_list,
  get_role_access,
  get_user_list,
  getAvailableAccess,
  getMemberByMobileNumber,
  inactive_user_list,
  update_role_access,
} from "../../../../Redux/Actions/action";
import { useLocation, useNavigate } from "react-router";
import TextLabel from "../../../Common/CommonTextLabel";
import CustomDialog from "../../../Common/CustomDialog";
import CommonTextField from "../../../Common/CommonTextField";
import AutoCompleteSearch from "../../../Common/AutoCompleteSearch";
import { BucketURL } from "../../../../ApiSetup/Api";
import sessionStoreUtil from "../../../../ApiSetup/Localstorage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomPagination from "../../../Common/CommonPagination";
import { useSelector } from "react-redux";
import CommonSearchField from "../../../Common/CommonSearchField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Role icon mapping
const getRoleIcon = (roleName) => {
  const name = roleName?.toLowerCase() || '';
  if (name.includes('super') || name.includes('admin')) return <AdminPanelSettingsIcon />;
  if (name.includes('senior') || name.includes('staff')) return <SupervisorAccountIcon />;
  if (name.includes('sarthi')) return <GroupIcon />;
  if (name.includes('account')) return <ManageAccountsIcon />;
  if (name.includes('verif')) return <SecurityIcon />;
  return <PersonIcon />;
};

// Role color mapping
const getRoleColor = (roleName) => {
  const name = roleName?.toLowerCase() || '';
  if (name.includes('super')) return '#7c3aed';
  if (name.includes('admin')) return '#3E4094';
  if (name.includes('senior')) return '#0891b2';
  if (name.includes('sarthi')) return '#059669';
  if (name.includes('account')) return '#d97706';
  return '#6366f1';
};

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  borderRadius: "12px !important",
  overflow: "hidden",
  marginBottom: 12,
  border: "none",
  boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
  transition: "all 0.2s ease-in-out",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    marginBottom: 16,
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  },
  "&:hover": {
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  minHeight: 48,
  "&.Mui-expanded": {
    minHeight: 48,
    backgroundColor: "#f1f5f9",
  },
  "& .MuiAccordionSummary-content": {
    margin: "8px 0",
    "&.Mui-expanded": {
      margin: "8px 0",
    },
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    transition: "transform 0.2s",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}));

const AccordionSummaryPermissions = styled(MuiAccordionSummary)(
  ({ theme }) => ({
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    minHeight: 72,
    padding: "8px 16px",
    "&.Mui-expanded": {
      minHeight: 72,
      backgroundColor: "#fafafa",
    },
    "& .MuiAccordionSummary-content": {
      margin: "0px",
      "&.Mui-expanded": {
        margin: "0px",
      },
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      transition: "transform 0.3s",
    },
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(180deg)",
    },
  })
);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 15,
    textAlign: "center", // Horizontally align content
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "2px !important",
    textAlign: "center", // Horizontally align content
  },
  // "&:last-of-type": {
  //     borderBottom: "none", // Remove bottom border for the last cell
  // },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-of-type td, &:last-of-type th": {
    borderBottom: "none", // Remove bottom border for the entire last row
  },
}));

const RolePermissions = () => {
  // ---------- Hook -----------
  const dispatch = useDispatch();
  const theme = useTheme();
  const { pathname } = useLocation();

  // --------------- State -------------
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [selectedPermissionRole, setSelectedPermissionRole] = useState(null); // Track which role's permissions to show
  const [data, setData] = useState();
  const [dynamicRoleList, setDynamicRoleList] = useState([]);
  const [page, setPage] = useState(1);
  // const [userListPage, setUserListPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState({
    index: null,
    selectedTab: "",
  });
  const [memberList, setMemberList] = useState([]);
  const [selectedRole, setSelectedRole] = useState({});
  const [searchValue, setSearchValue] = useState({});
  const [serachUserList, setSerachUserList] = useState([]);
  const [error, setError] = useState({});
  const [open, setOpen] = useState(false);
  const [addRoleData, setAddRoleData] = useState({
    newRole: "",
    organisationName: "",
  });
  const [timer, setTimer] = useState(0);
  const user = sessionStoreUtil.getLSItem("userData")
    ? JSON.parse(sessionStoreUtil.getLSItem("userData"))
    : {};
  const [isInActive, setIsInActive] = useState(false);
  const [inActiveList, setInActiveList] = useState([]);
  const [searchText, setSearchText] = useState("");

  const dataAccess = useSelector(
    (value) => value?.allAvailableAccess?.AllAvailableAccessList
  );

  // --------------- handleChange ---------------
  // Handle click on the "Permissions" button - now accepts role parameter
  const handlePermissionsClick = (roleName) => {
    setSelectedPermissionRole(roleName);
    setIsTableVisible(true);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const configDynamicRole =
    user.loginRole !== "SuperAdmin"
      ? dynamicRoleList?.filter((e) => e?.userType != "SuperAdmin")
      : dynamicRoleList;

  const handleChange = (pageIndex, actionIndex, role) => {
    setData((prevData) => {
      const newData = [...prevData]; // Clone current state
      const page = newData[pageIndex]; // Get the specific page
      const action = page.actions[actionIndex]; // Get the specific action

      // Toggle the role value
      action.roles[role] = !action.roles[role];

      // Prepare payload to send the entire page's updated data
      const updatedPage = {
        pageName: page.pageName,
        actions: page.actions.map((action) => ({
          actionName: action.actionName,
          roles: action.roles,
        })),
      };

      // Make API call to update the page data
      updatePageAccess(updatedPage);

      return newData; // Update the state
    });
  };

  const handleAccordionClick = (userName, index) => {
    if (openAccordion === index) {
      setActiveAccordion({ index: null, selectedTab: "" });
      setOpenAccordion(null);
      setMemberList([]);
    } else {
      setSerachUserList([]);
      setMemberList([]);
      setActiveAccordion({ index: index, selectedTab: userName });
      setOpenAccordion(index);
      _getUserList(userName);
    }
  };

  const handleClear = () => {
    setOpen(false);
    setAddRoleData({ newRole: "" });
    setError({});
  };

  // --------------- API---------------

  const _getRoleList = async () => {
    dispatch(showLoader(true));
    let body = {
      // page: page,
      // limit: rowsPerPage
    };
    try {
      const result = await dispatch(get_role_access(body));
      const error = ErrorHandler(result);
      if (error) {
        setData(result?.payload?.data?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const updatePageAccess = async (updatedPage) => {
    try {
      dispatch(showLoader(true));
      const body = {
        pageName: updatedPage?.pageName,
        actions: updatedPage?.actions,
      };
      const result = await dispatch(update_role_access(body)); // API call
      const error = ErrorHandler(result);

      if (error) {
        toast.success(
          result?.payload?.message || "Page data updated successfully"
        );
        await _getRoleList();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update page data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _getDynamicRoleList = async () => {
    dispatch(showLoader(true));
    let body = {
      // page: page,
      // limit: rowsPerPage
    };
    try {
      const result = await dispatch(get_dynamic_role_list(body));
      const error = ErrorHandler(result);
      if (error) {
        setDynamicRoleList(result?.payload?.data?.response);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _getUserList = async (userName) => {
    dispatch(showLoader(true));
    let body = {
      userType: userName,
      page: page,
      limit: rowsPerPage,
    };
    try {
      const result = await dispatch(get_user_list(body));
      const error = ErrorHandler(result);
      if (error) {
        setMemberList(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _getInActiveUserList = async () => {
    dispatch(showLoader(true));
    let body = {
      userType: "User",
      page: page,
      limit: rowsPerPage,
      search: searchText,
    };
    try {
      const result = await dispatch(inactive_user_list(body));
      const error = ErrorHandler(result);
      if (error) {
        setInActiveList(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _getAllMember = async (search) => {
    dispatch(showLoader(true));
    let body = {
      page: 1,
      limit: 10,
      search: search,
    };
    try {
      const result = await dispatch(getMemberByMobileNumber(body));
      const error = ErrorHandler(result);
      if (error) {
        setSearchValue(search ? search : "");
        setSerachUserList(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _addExtraUserType = async (role, organisationName) => {
    if (!selectedRole) {
      setError({ role: "please select any one member" });
      return;
    }
    try {
      dispatch(showLoader(true));
      const body = {
        _id: selectedRole?._id,
        userType: role,
        organisationName: organisationName,
      };
      const result = await dispatch(add_extra_userType(body)); // API call
      const error = ErrorHandler(result);
      if (error) {
        
        setSelectedRole({});
        toast.success(
          result?.payload?.message || "Page data updated successfully"
        );
        _getDynamicRoleList();
        _getUserList(role);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update page data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _active_inactive_member = async (id, role) => {
    try {
      dispatch(showLoader(true));
      const body = {
        _id: id,
      };
      const result = await dispatch(active_inactive_member(body)); // API call
      const error = ErrorHandler(result);
      if (error) {
        toast.success(
          result?.payload?.message || "Page data updated successfully"
        );
        isInActive ? _getInActiveUserList() : _getUserList(role);
        _getUserList(role);
        _getDynamicRoleList();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update page data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _active_inactive_member_Deactive = async (id, role) => {
    try {
      dispatch(showLoader(true));
      const body = {
        _id: id,
        isDeleted: false,
      };
      const result = await dispatch(active_inactive_member_Deactive(body)); // API call
      const error = ErrorHandler(result);
      if (error) {
        toast.success(
          result?.payload?.message || "Page data updated successfully"
        );
        isInActive ? _getInActiveUserList() : _getUserList(role);
        _getUserList(role);
        _getDynamicRoleList();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update page data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _addNewRole = async () => {
    if (!addRoleData?.newRole || !addRoleData?.organisationName) {
      setError({
        newRole: "*Please enter role",
        organisationName: "*Please enter organisation",
      });
      return;
    }
    dispatch(showLoader(true));
    try {
      let body = {
        roleName: addRoleData?.newRole,
        organisationName: addRoleData?.organisationName,
      };
      const result = await dispatch(addRole(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _getDynamicRoleList();
        handleClear();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // ---------- useEffect ------------
  useEffect(() => {
    _getRoleList();
    _getDynamicRoleList();
  }, []);

  useEffect(() => {
    if (isInActive) {
      _getInActiveUserList();
    }
  }, [page, rowsPerPage, searchText, isInActive]);

  useEffect(() => {
    if (activeAccordion.selectedTab) {
      _getUserList(activeAccordion.selectedTab);
    }
  }, [page, rowsPerPage, activeAccordion.selectedTab]);

  
  return (
    <>
      <Box
        sx={{
          backgroundColor: "#f8f8f8",
          padding: "18px",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {(isTableVisible || isInActive) && (
                <ArrowBackIcon
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: "25px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setIsTableVisible(false);
                    setSelectedPermissionRole(null);
                    _getUserList(activeAccordion.selectedTab);
                    setIsInActive(false);
                    setSearchText("");
                  }}
                />
              )}
              <Box>
                <Typography sx={{ fontSize: "22px", fontWeight: 600, color: '#1e293b' }}>
                  {isInActive ? "Deactivated Users" : isTableVisible ? `${selectedPermissionRole} Permissions` : "Role & Permission"}
                </Typography>
                {!isInActive && !isTableVisible && (
                  <Typography variant="body2" color="text.secondary">
                    Manage user roles and their permissions
                  </Typography>
                )}
                {isTableVisible && selectedPermissionRole && (
                  <Typography variant="body2" color="text.secondary">
                    Configure page access permissions for {selectedPermissionRole} role
                  </Typography>
                )}
              </Box>
            </Box>
            {isInActive && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 1.5, 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '8px',
                  border: '1px solid #fcd34d',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="#92400e">
                  <strong>Note:</strong> Below users are deactivated. Click the ✓ icon on each chip to reactivate them.
                </Typography>
              </Paper>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {checkAccess(pathname, dataAccess, "Deactivated Users") &&
              !isInActive && (
                <ButtonOutlined
                  variant={"text"}
                  height="38px"
                  borderRadius={"8px"}
                  color={"primary"}
                  text="Deactivated Users"
                  onClick={() => {
                    setIsInActive(true);
                    _getInActiveUserList();
                  }}
                  sx={{
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': {
                      backgroundColor: '#f8fafc',
                      borderColor: '#cbd5e1',
                    },
                  }}
                />
              )}
            {checkAccess(pathname, dataAccess, "AddNewRole") && !isInActive && (
              <ButtonContained
                text="+ Add New Role"
                height={"38px"}
                borderRadius={"8px"}
                onClick={() => setOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #3E4094 0%, #6366f1 100%)',
                  boxShadow: '0 2px 4px rgba(62, 64, 148, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #363785 0%, #5558e3 100%)',
                  },
                }}
              />
            )}
          </Box>
          {isInActive && (
            <CommonSearchField
              placeholder={"Search.."}
              tooltipList={["First Name", "Last Name"]}
              handleSearch={(e) => {
                setPage(1);
                setSearchText(e);
              }}
            />
          )}
        </Box>

        {/* Roles List */}

        {isInActive ? (
          <>
            <Box display={"flex"} gap={1} flexWrap={"wrap"}>
              {inActiveList?.response?.filter(chip => chip?.personalDetails?.fullName)?.map((chip, i) => {
                const name = chip?.personalDetails?.fullName;
                return (
                  <Chip
                    key={chip?._id || i}
                    label={capitalizeFirstLetterOfWords(name)}
                    onDelete={() =>
                      _active_inactive_member_Deactive(chip?._id, "User")
                    }
                    deleteIcon={<CheckCircleIcon />}
                    sx={{
                      "& .MuiChip-label ": {
                        color: theme.palette.primary.main,
                      },
                      "& .MuiChip-deleteIcon ": {
                        color: theme.palette.success.light,
                      },
                      height: "30px",
                    }}
                  />
                );
              })}
            </Box>
            {inActiveList?.response?.length > 0 && (
              <Box>
                <CustomPagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={Math.ceil(inActiveList?.count / rowsPerPage)}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            )}
          </>
        ) : isTableVisible && selectedPermissionRole ? (
          <>
            <Box my={2}>
              {/* Role-specific permission header */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: `${getRoleColor(selectedPermissionRole)}10`,
                  borderRadius: '12px',
                  border: `1px solid ${getRoleColor(selectedPermissionRole)}30`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: `${getRoleColor(selectedPermissionRole)}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: getRoleColor(selectedPermissionRole),
                  }}
                >
                  {getRoleIcon(selectedPermissionRole)}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: getRoleColor(selectedPermissionRole) }}>
                    {selectedPermissionRole}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toggle checkboxes to enable/disable page access for this role
                  </Typography>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                <Table>
                  <TableHead
                    sx={{
                      backgroundColor: theme?.palette?.primary?.main,
                      "& th": { color: "#fff" },
                    }}
                  >
                    <TableRow>
                      <StyledTableCell
                        align="left"
                        sx={{
                          padding: "12px 16px",
                          fontWeight: 600,
                          width: '200px',
                        }}
                      >
                        Page
                      </StyledTableCell>
                      <StyledTableCell
                        align="left"
                        sx={{ padding: "12px 16px", fontWeight: 600 }}
                      >
                        Permissions
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.map((page, pageIndex) => {
                      // Get actions that have this role
                      const roleActions = page?.actions?.filter(action => 
                        action.roles.hasOwnProperty(selectedPermissionRole)
                      );
                      
                      if (roleActions?.length === 0) return null;

                      return (
                        <StyledTableRow key={page._id}>
                          <StyledTableCell 
                            sx={{ 
                              textAlign: "left", 
                              padding: "12px 16px",
                              verticalAlign: 'top',
                              borderBottom: '1px solid #e2e8f0',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: theme?.palette?.primary?.main,
                              }}
                            >
                              {page?.pageName}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell 
                            sx={{ 
                              textAlign: "left", 
                              padding: "8px 16px",
                              borderBottom: '1px solid #e2e8f0',
                            }}
                          >
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {page?.actions?.map((action, actionIndex) => {
                                const hasRole = action.roles.hasOwnProperty(selectedPermissionRole);
                                if (!hasRole) return null;
                                const isChecked = action.roles[selectedPermissionRole] || false;

                                return (
                                  <Chip
                                    key={action._id}
                                    label={action?.actionName}
                                    size="small"
                                    onClick={() =>
                                      handleChange(
                                        pageIndex,
                                        actionIndex,
                                        selectedPermissionRole
                                      )
                                    }
                                    icon={
                                      <Checkbox
                                        checked={isChecked}
                                        size="small"
                                        sx={{
                                          padding: 0,
                                          marginLeft: '8px',
                                          color: isChecked ? '#fff' : '#94a3b8',
                                          '&.Mui-checked': {
                                            color: '#fff',
                                          },
                                        }}
                                        onChange={() =>
                                          handleChange(
                                            pageIndex,
                                            actionIndex,
                                            selectedPermissionRole
                                          )
                                        }
                                      />
                                    }
                                    sx={{
                                      height: 32,
                                      backgroundColor: isChecked 
                                        ? getRoleColor(selectedPermissionRole) 
                                        : '#f1f5f9',
                                      color: isChecked ? '#fff' : '#64748b',
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        backgroundColor: isChecked 
                                          ? getRoleColor(selectedPermissionRole) 
                                          : '#e2e8f0',
                                        transform: 'scale(1.02)',
                                      },
                                      '& .MuiChip-label': {
                                        paddingRight: '12px',
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </>
        ) : (
          <>
            {configDynamicRole?.map((role, index) => {
              const roleColor = getRoleColor(role?.userType);
              return (
                <Accordion
                  key={index}
                  elevation={0}
                  expanded={activeAccordion.index === index}
                >
                  <AccordionSummaryPermissions
                    onClick={() => {
                      handleAccordionClick(role?.userType, index);
                      setSelectedRole({});
                    }}
                    expandIcon={
                      <Box
                        sx={{
                          transform: activeAccordion.index === index ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                          color: roleColor,
                        }}
                      >
                        <ExpandMoreIcon />
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pr: 2,
                      }}
                    >
                      <Box display={"flex"} alignItems={"center"} gap={2}>
                        {/* Role Icon */}
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            backgroundColor: `${roleColor}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: roleColor,
                          }}
                        >
                          {getRoleIcon(role?.userType)}
                        </Box>
                        <Box>
                          <Typography
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: "16px",
                              color: '#1e293b',
                            }}
                          >
                            {role?.userType}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              size="small"
                              icon={<GroupIcon sx={{ fontSize: 14 }} />}
                              label={`${role?.totalCount || 0} Members`}
                              sx={{
                                height: 24,
                                fontSize: '12px',
                                backgroundColor: `${roleColor}15`,
                                color: roleColor,
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  color: roleColor,
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      {checkAccess(pathname, dataAccess, "Permissions") && (
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <ButtonOutlined
                            variant={"text"}
                            height="36px"
                            borderRadius={"8px"}
                            startIcon={<KeyIcon sx={{ fontSize: 18 }} />}
                            text="Permissions"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePermissionsClick(role?.userType);
                            }}
                            sx={{
                              borderColor: roleColor,
                              color: roleColor,
                              '&:hover': {
                                backgroundColor: `${roleColor}10`,
                                borderColor: roleColor,
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </AccordionSummaryPermissions>

                  <AccordionDetails sx={{ padding: "16px", backgroundColor: '#fafafa', borderTop: '1px solid #e2e8f0' }}>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                    >
                          {role?.userType !== "User" && (
                            <>
                              <Grid item xs={12}>
                                <Box sx={{ 
                                  p: 2, 
                                  backgroundColor: '#fff', 
                                  borderRadius: '10px', 
                                  border: '1px solid #e2e8f0',
                                }}>
                                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                                    Assign New User to {role?.userType}
                                  </Typography>
                                  <Grid container spacing={2} alignItems="flex-start">
                                    <Grid item xs={12} md={8}>
                                      <AutoCompleteSearch
                                        width={"100%"}
                                        placeholder={"Type name or phone to search users..."}
                                        handleChange={(e, newValue) => {
                                          if (!newValue) {
                                            _getAllMember();
                                          } else {
                                            setSelectedRole(newValue);
                                          }
                                        }}
                                        options={serachUserList?.response || []}
                                        getOptionLabel={(option) =>
                                          capitalizeFirstLetterOfWords(
                                            option?.fullName
                                          ) ||
                                          option?.phoneNumber ||
                                          ""
                                        }
                                        handleSearch={(ev) => {
                                          let val = ev?.target?.value || "";
                                          clearTimeout(timer);
                                          setTimer(
                                            setTimeout(() => {
                                              _getAllMember(val);
                                            }, 300)
                                          );
                                        }}
                                        searchValue={searchValue || ""}
                                        name="selectedRole"
                                        defaultValue={selectedRole || null}
                                        value={selectedRole || null}
                                        labelSize={"14px"}
                                        fontWeight={400}
                                        filterOptions={(options) => options}
                                        noOptionsText={
                                          <Box sx={{ p: 1, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                              Type to search for users by name or phone
                                            </Typography>
                                          </Box>
                                        }
                                        renderOption={(props, option) => {
                                          return (
                                            <li
                                              {...props}
                                              style={{
                                                padding: '10px 12px',
                                                borderBottom: '1px solid #f1f5f9',
                                              }}
                                            >
                                              <Box display={"flex"} gap={1.5} alignItems="center" width="100%">
                                                <Avatar
                                                  src={BucketURL + option?.avtar}
                                                  alt={option?.fullName}
                                                  sx={{ 
                                                    width: 40, 
                                                    height: 40,
                                                    bgcolor: '#3E4094',
                                                    fontSize: 16,
                                                  }}
                                                >
                                                  {option?.fullName?.charAt(0)?.toUpperCase()}
                                                </Avatar>
                                                <Box flex={1}>
                                                  <Typography
                                                    sx={{
                                                      color: '#1e293b',
                                                      fontSize: '14px',
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    {capitalizeFirstLetterOfWords(option?.fullName)}
                                                  </Typography>
                                                  <Box display={"flex"} gap={1} alignItems="center">
                                                    <Typography
                                                      sx={{
                                                        color: '#64748b',
                                                        fontSize: '12px',
                                                      }}
                                                    >
                                                      📞 +91 {option?.phoneNumber}
                                                    </Typography>
                                                    <CommonChip
                                                      label={option?.verificationStatus}
                                                      status={option?.verificationStatus}
                                                      height={"18px"}
                                                      fontSize="11px"
                                                    />
                                                  </Box>
                                                </Box>
                                              </Box>
                                            </li>
                                          );
                                        }}
                                      />
                                      {error?.role && !selectedRole && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                          {error?.role}
                                        </Typography>
                                      )}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <ButtonContained
                                        width={"100%"}
                                        text={"+ Assign Role"}
                                        padding={"12px 16px"}
                                        fontSize={{ xs: "13px", md: "14px" }}
                                        height={"42px"}
                                        borderRadius={"8px"}
                                        onClick={() =>
                                          _addExtraUserType(
                                            role?.userType,
                                            role?.organisationName
                                          )
                                        }
                                        disabled={!selectedRole?._id}
                                        sx={{
                                          background: selectedRole?._id 
                                            ? 'linear-gradient(135deg, #3E4094 0%, #6366f1 100%)' 
                                            : '#e2e8f0',
                                          '&:disabled': {
                                            background: '#e2e8f0',
                                            color: '#94a3b8',
                                          },
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            </>
                          )}

                          {/* Members Section */}
                          {memberList?.users?.length > 0 && (
                            <>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Assigned Members ({memberList?.users?.length || 0})
                                  </Typography>
                                  <TextField
                                    size="small"
                                    placeholder="Search members..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{
                                      width: { xs: '100%', sm: '220px' },
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        backgroundColor: '#fff',
                                        fontSize: '14px',
                                        '& fieldset': {
                                          borderColor: '#e2e8f0',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: '#cbd5e1',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#3E4094',
                                        },
                                      },
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box 
                                  sx={{ 
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    p: 1.5,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '10px',
                                    border: '1px solid #e2e8f0',
                                  }}
                                >
                                  <Grid container spacing={1}>
                                    {memberList?.users
                                      ?.filter(chip => 
                                        !searchText || 
                                        chip?.fullName?.toLowerCase()?.includes(searchText.toLowerCase())
                                      )
                                      ?.map((chip, i) => {
                                        const name = chip?.fullName;
                                        return (
                                          <Grid item key={chip?._id || i}>
                                            <Chip
                                              avatar={
                                                <Avatar sx={{ bgcolor: '#3E4094', width: 24, height: 24, fontSize: 12 }}>
                                                  {name?.charAt(0)?.toUpperCase()}
                                                </Avatar>
                                              }
                                              label={capitalizeFirstLetterOfWords(name)}
                                              onDelete={() =>
                                                _active_inactive_member(
                                                  chip?._id,
                                                  role?.userType
                                                )
                                              }
                                              sx={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #e2e8f0',
                                                "& .MuiChip-label": {
                                                  color: '#1e293b',
                                                  fontWeight: 500,
                                                  fontSize: '13px',
                                                },
                                                "& .MuiChip-deleteIcon": {
                                                  color: '#ef4444',
                                                  '&:hover': {
                                                    color: '#dc2626',
                                                  },
                                                },
                                                height: "36px",
                                                '&:hover': {
                                                  backgroundColor: '#f1f5f9',
                                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                },
                                              }}
                                            />
                                          </Grid>
                                        );
                                      })}
                                    {memberList?.users?.filter(chip => 
                                      !searchText || 
                                      chip?.fullName?.toLowerCase()?.includes(searchText.toLowerCase())
                                    )?.length === 0 && (
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                          No members found matching "{searchText}"
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </Grid>
                            </>
                          )}

                          {role?.userType === "User" && (
                            <Grid item xs={12}>
                              {dynamicRoleList?.length > 0 && (
                                <Grid item xs={12}>
                                  <CustomPagination
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    count={Math.ceil(
                                      role?.totalCount / rowsPerPage
                                    )}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={
                                      handleChangeRowsPerPage
                                    }
                                  />
                                </Grid>
                              )}
                            </Grid>
                          )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}
      </Box>

      {open && (
        <CustomDialog
          isOpen={open}
          handleClose={handleClear}
          title={"Add New Role"}
          padding={"10px"}
          width={400}
          action={
            <ButtonContained
              text={"Add Role"}
              onClick={_addNewRole}
              minWidth={"100%"}
              marginTop={"10px"}
            />
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CommonTextField
                value={addRoleData?.newRole || ''}
                width={"100%"}
                text="Role Name"
                type="text"
                onChange={(e) => {
                  setAddRoleData({ ...addRoleData, newRole: e.target.value });
                  setError({ ...error, newRole: '' });
                }}
                placeholder="Enter Role Name (e.g., Manager, Verifier)"
                name="newRole"
                valid
              />
              <TextLabel
                title={error?.newRole || ""}
                color="error"
              />
            </Grid>
            <Grid item xs={12}>
              <CommonTextField
                value={addRoleData?.organisationName || ''}
                width={"100%"}
                text="Organisation Name"
                type="text"
                onChange={(e) => {
                  setAddRoleData({
                    ...addRoleData,
                    organisationName: e.target.value,
                  });
                  setError({ ...error, organisationName: '' });
                }}
                placeholder="Enter Organisation Name"
                name="organisationName"
                valid
              />
              <TextLabel
                title={error?.organisationName || ""}
                color="error"
              />
            </Grid>
          </Grid>
        </CustomDialog>
      )}
    </>
  );
};

export default RolePermissions;
